import Cookies from 'js-cookie'

// MOB-077 — the persistence cookies (orderrId, location, pointSell, the check-in/check-out
// dates, the ServerClock offset) carry the cart across a reload, so their attributes decide
// whether the cart survives and who may send it.
//
// `Secure` cannot be unconditional: the dev server runs on plain http://localhost:8081 and a
// Secure cookie is dropped there, which would empty the cart on every reload. It is derived
// from the scheme the page is actually served over, so a production https deployment gets it
// and local http development does not — no build flag has to be kept in sync.
//
// The `SameSite` VALUE is deployment configuration, not a constant: `Lax` is the default and
// the only value that is safe for a top-level storefront. A frame on a site that the tenant
// lists and that has another registrable domain needs `None` (MOB-076, ADR 0014), which
// browsers accept only together with `Secure`. A same-site frame keeps the cart with `Lax`.
// Override with VITE_COOKIE_SAMESITE=Lax|Strict|None.

const EXPIRES_DAYS = 7

/**
 * Normalizes a configured SameSite value to a token the browser accepts.
 * Anything unrecognized (unset, typo) falls back to the safe default.
 *
 * @param {string} [configured]
 * @returns {'Lax'|'Strict'|'None'}
 */
export function resolveSameSite (configured) {
  switch (String(configured || '').trim().toLowerCase()) {
    case 'strict':
      return 'Strict'
    case 'none':
      return 'None'
    default:
      return 'Lax'
  }
}

/**
 * The attributes every persistence cookie is written with.
 *
 * @param {object} [input]
 * @param {string} [input.configuredSameSite] raw VITE_COOKIE_SAMESITE value
 * @param {string} [input.protocol] `window.location.protocol` of the page doing the write
 * @returns {{ expires: number, sameSite: string, secure: boolean }}
 */
export function cookieAttributes ({ configuredSameSite, protocol } = {}) {
  const secure = protocol === 'https:'
  let sameSite = resolveSameSite(configuredSameSite)

  // `SameSite=None` without `Secure` is rejected outright by current browsers, so an
  // embedded configuration served over plain http would lose the cart entirely. Degrade to
  // the default instead of writing a cookie that is never stored.
  if (sameSite === 'None' && !secure) sameSite = 'Lax'

  return { expires: EXPIRES_DAYS, sameSite, secure }
}

function currentAttributes () {
  return cookieAttributes({
    configuredSameSite: import.meta.env.VITE_COOKIE_SAMESITE,
    protocol: typeof window !== 'undefined' ? window.location && window.location.protocol : undefined,
  })
}

function parse (raw) {
  if (raw === undefined || raw === null) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

const cookies = {
  set (name, value, opts) {
    const stored = typeof value === 'string' ? value : JSON.stringify(value)
    Cookies.set(name, stored, { ...currentAttributes(), ...(opts || {}) })
  },
  get (name) {
    return parse(Cookies.get(name))
  },
  remove (name) {
    // The deletion cookie is matched on name/path/domain, but it is still a Set-Cookie: over
    // https it must carry the same attributes or the browser refuses to store it.
    Cookies.remove(name, currentAttributes())
  },
}

export default cookies
